import React, { useState } from 'react';
import { Box, Button, FormControl, FormLabel, RadioGroup, Radio, Checkbox } from '@chakra-ui/react';

import { Modal, Textarea } from 'shared-components';

import { DocAccessType, IFinanceDocuments } from '../../../../types';
import { ENROLY_AGENT_ROLES, UNI_ROLES, AGENT_ROLES } from '../../../../store/auth/types';
import { IDocument, IMessage, DocStatus } from 'types';

export interface IDispatchProps {
  onClose: () => void;
  onRejectDoc: (
    message: IMessage,
    sponsorType: string,
    sponsorKey: string | null,
    docType: string | null,
    docKey: string | null,
    reason: string,
    status: IDocument['status'],
    isFinDoc: boolean,
    isFollowUpDocs: boolean,
    isCustomDocRequested: boolean,
  ) => void;
}

export interface IProps {
  financeDocuments: IFinanceDocuments;
  sponsorType: string;
  docType: string | null;
  sponsorKey: string | null;
  docKey: string | null;
  modalTitle?: string;
  userName: string;
  displayName: string;
  docUserAccess?: DocAccessType;
  isFinanceDocumentModal: boolean;
  isFollowUpDocumentModal: boolean;
  role: string;
  requestNewDocumentDefault: boolean;
}

type ICustomProps = IProps & IDispatchProps;

const FileRejectModal = (props: ICustomProps) => {
  const {
    sponsorType,
    docType,
    sponsorKey,
    docKey = null,
    modalTitle = '',
    userName,
    displayName,
    docUserAccess,
    isFinanceDocumentModal,
    isFollowUpDocumentModal,
    role,
    requestNewDocumentDefault,
  } = props;

  // Special case: if rejecting a bank statement,
  // then give the option to request a bank letter instead
  const hasCustomRequestDoc = docType === 'bankStatement';

  const initialMessage: IMessage = {
    message: '',
    isSendToStudent: true,
    messageType: 'note',
    createdAt: 0,
    author: '',
    authorName: '',
    messageFrom: 'uni',
    messageTo: 'student',
  };
  const [message, setMessage] = useState(initialMessage);
  const [isSendToStudent, setIsSendToStudent] = useState(true);
  const [isSendToAgent, setIsSendToAgent] = useState(true);
  const [isCustomDocRequested, setIsCustomDocRequested] = useState(false);
  const [requestNewDocument, setRequestNewDocument] = useState(requestNewDocumentDefault);

  const requestNewDocumentHandler = () => setRequestNewDocument(!requestNewDocument);

  const submitMessage = () => {
    if (!message) {
      return;
    }
    const author = userName || 'Anonymous';
    const msgTitle = modalTitle;
    const msgReason = message.message ? `Rejected: ${message.message}` : 'Rejected';
    const getMessageTo = (messageFrom = '') => {
      if (!docUserAccess && isSendToStudent) {
        return 'student';
      }
      if (docUserAccess === DocAccessType.AGENT_ONLY && isSendToAgent) {
        return 'agent';
      }
      return messageFrom;
    };

    const getMessageFrom = () => {
      if (ENROLY_AGENT_ROLES.includes(props.role)) {
        return 'enroly';
      }
      return 'uni';
    };
    const messageFrom = getMessageFrom();
    const newMessage: IMessage = {
      message: `${msgTitle} ${msgReason}`,
      createdAt: Date.now(),
      isSendToStudent: !docUserAccess && isSendToStudent,
      messageType:
        docUserAccess === DocAccessType.AGENT_ONLY && isSendToAgent
          ? 'agentUni'
          : isSendToStudent
            ? 'note'
            : 'personalNote',
      author,
      authorName: displayName || author,
      messageFrom,
      messageTo: getMessageTo(messageFrom),
      ...(UNI_ROLES.includes(role) ? { uniRead: true } : {}),
      ...(ENROLY_AGENT_ROLES.includes(role) ? { adminRead: true } : {}),
      ...(AGENT_ROLES.includes(role) ? { agentRead: true } : {}),
    };

    props.onRejectDoc(
      newMessage,
      sponsorType,
      sponsorKey,
      docType,
      docKey,
      message.message,
      // if new document is requested, reject current doc and not count it in total
      // if no new document is requested, reject document has to be counted
      requestNewDocument ? DocStatus.REJECTED : DocStatus.REJECTED_WITHOUT_ALTERNATIVE,
      isFinanceDocumentModal,
      isFollowUpDocumentModal,
      isCustomDocRequested,
    );

    props.onClose();
  };

  return (
    <Modal
      id={'file-reject'}
      show={true}
      title={'Rejection reason'}
      onClose={props.onClose}
      footer={
        <Button id="submit-log-button" onClick={submitMessage} variant="solid" size="lg">
          Reject file
        </Button>
      }
    >
      <Textarea
        id={'message'}
        label={'Message'}
        value={message.message}
        onChange={(event: React.FormEvent<HTMLTextAreaElement>) =>
          setMessage({ ...message, message: event.currentTarget.value })
        }
        placeholder={'Type your message here...'}
      />

      {!docUserAccess && (
        <Box>
          <Checkbox
            id={'reject-doc-send-msg-to-student'}
            key={'reject-doc-send-msg-to-student'}
            isChecked={isSendToStudent}
            onChange={() => setIsSendToStudent(!isSendToStudent)}
            size="lg"
          >
            Send this note to student
          </Checkbox>
        </Box>
      )}

      {docUserAccess === DocAccessType.AGENT_ONLY && (
        <Box>
          <Checkbox
            id={'reject-doc-send-msg-to-agent'}
            key={'reject-doc-send-msg-to-agent'}
            isChecked={isSendToAgent}
            onChange={() => setIsSendToAgent(!isSendToAgent)}
            size="lg"
          >
            Send this note to agent
          </Checkbox>
        </Box>
      )}

      <Box>
        <Checkbox
          id={'reject-doc-req-new-doc'}
          key={'reject-doc-req-new-doc'}
          isChecked={requestNewDocument}
          onChange={requestNewDocumentHandler}
          size="lg"
        >
          Request new document
        </Checkbox>
      </Box>

      {requestNewDocument && hasCustomRequestDoc && (
        <FormControl mt={6}>
          <FormLabel as="legend" fontSize="lg">
            Request new
          </FormLabel>

          <RadioGroup display="flex" flexDirection={{ base: 'column', lg: 'row' }} gap={2}>
            <Radio
              key="no"
              id="request-new-statement"
              name={'customRequestRadios'}
              isChecked={!isCustomDocRequested}
              onChange={(_evt: React.FormEvent<HTMLInputElement>) => setIsCustomDocRequested(false)}
              variant="outline"
              size="lg"
              flex="1 1 50%"
            >
              Statement
            </Radio>

            <Radio
              key="yes"
              id="request-bank-letter"
              name={'customRequestRadios'}
              isChecked={isCustomDocRequested}
              onChange={(_evt: React.FormEvent<HTMLInputElement>) => setIsCustomDocRequested(true)}
              variant="outline"
              size="lg"
              flex="1 1 50%"
            >
              Bank letter
            </Radio>
          </RadioGroup>
        </FormControl>
      )}
    </Modal>
  );
};

export default FileRejectModal;
